import { useEffect, useState } from 'react'
import { rememberDisplayName } from '@/src/renderer/lib/profile/rememberDisplayName'
import type { User } from '@supabase/supabase-js'
import { db } from '../lib/db'
import type { Avatar } from '../lib/db/types'
import type { UserDisplayProfile } from '../lib/profile/displayProfile'
import { supabase } from '../lib/supabase/client'
import { getAvatarImageUrl } from '../lib/supabase/avatars'
import { onAppEvent } from '../lib/events'
import { isThrottleElapsed } from '../lib/sync/throttle'

/**
 * Wie oft der Hook höchstens neu nachfragt, wenn das Fenster wieder in den
 * Blick kommt. Fokus feuert beim Hin- und Herschalten schnell hintereinander.
 */
const REFOCUS_REFRESH_MS = 30_000

export type { UserDisplayProfile } from '../lib/profile/displayProfile'

/**
 * Last successfully-resolved profile, cached at module scope. The hook is
 * per-component (sidebar, settings, every GoalsView), so without this each
 * consumer would re-run the full async resolve (`supabase.auth.getUser` session
 * hydration + `profiles` SELECT + Dexie lookups) AND render at least one
 * `isLoading: true` frame — making the bundled default name/avatar visibly
 * flash in before the real profile arrives. Seeding state synchronously from
 * this cache lets a later consumer mount with the data already present. Only
 * size-independent fields are cached; the avatar URL is derived per consumer
 * from `avatar` + `displaySize`.
 */
interface ResolvedProfileCache {
  avatar: Avatar | null
  fullName: string | null
  avatarPresetId: string | null
  email: string | null
}
let resolvedCache: ResolvedProfileCache | null = null

/**
 * Drop the cached profile so the next mount re-resolves from scratch. Called by
 * `wipeUserLocalState` on logout / user-switch, so a stale profile from the
 * previous user can never be seeded into a fresh session.
 */
export function clearUserDisplayProfileCache(): void {
  resolvedCache = null
}

function profileFromCache(cache: ResolvedProfileCache, displaySize: number): UserDisplayProfile {
  return {
    avatarUrl: cache.avatar ? getAvatarImageUrl(cache.avatar, displaySize) : null,
    fullName: cache.fullName,
    avatarPresetId: cache.avatarPresetId,
    email: cache.email,
    isLoading: false,
  }
}

/**
 * Resolves the user's selected avatar preset and full name for sidebar display.
 *
 * Looks up `avatar_preset_id` and `full_name` on the user's profile (Supabase
 * online, Dexie offline) and resolves the avatar id to a CDN URL or cached
 * blob via {@link getAvatarImageUrl}. `useGlobalUser` is unreliable here
 * because the main-process `auth:success` IPC is never emitted, so we go
 * straight to the source.
 *
 * @param userId Supabase user id, or `'offline-user'` for the local profile.
 * @param displaySize Logical px size the avatar will be rendered at.
 */
export function useUserDisplayProfile(
  userId: string | undefined,
  displaySize: number = 32
): UserDisplayProfile {
  // Seed synchronously from the module cache when a previous consumer already
  // resolved the profile — that consumer's data is reused on the first render
  // (isLoading: false), so the name/avatar never flash in over the default.
  const [profile, setProfile] = useState<UserDisplayProfile>(() =>
    resolvedCache
      ? profileFromCache(resolvedCache, displaySize)
      : { avatarUrl: null, fullName: null, avatarPresetId: null, email: null, isLoading: true },
  )

  useEffect(() => {
    let cancelled = false
    let unsubscribe: (() => void) | null = null

    let lastLoadAt = 0
    const load = async () => {
      lastLoadAt = Date.now()
      try {
        let presetId: string | null | undefined
        let fullName: string | null | undefined

        // Resolve the authenticated user via supabase.auth.getUser() — this
        // awaits session hydration (encrypted safeStorage IPC in Electron),
        // so the RLS-gated profiles SELECT actually has auth.uid() set.
        let authUser: User | null = null
        if (supabase) {
          try {
            const { data } = await supabase.auth.getUser()
            authUser = data.user
          } catch {
            authUser = null
          }
        }

        if (authUser && supabase) {
          fullName = authUser.user_metadata?.full_name ?? null

          const { data } = await supabase
            .from('profiles')
            .select('avatar_preset_id, full_name')
            .eq('id', authUser.id)
            .maybeSingle()
          presetId = data?.avatar_preset_id
          if (!fullName) fullName = data?.full_name ?? null
        }

        if (!presetId || !fullName) {
          const lookupId = authUser?.id
            ?? (userId && userId !== 'offline-user' ? userId : 'offline-user')
          const localProfile = await db.userProfiles.get(lookupId)
          if (!presetId) presetId = localProfile?.avatar_preset_id
          if (!fullName) fullName = localProfile?.fullName ?? null
        }

        // Resolve the avatar ROW (size-independent) so it can be cached and the
        // URL re-derived per consumer/displaySize.
        let avatar: Avatar | null = null
        if (presetId) {
          avatar = (await db.avatars.get(presetId)) ?? null
          // Cold-cache fallback: a freshly-installed app has an empty
          // local avatar catalog because the user never opened the
          // AvatarPicker (`loadAvatars` is what populates Dexie). Without
          // this branch the sidebar / profile section would render the
          // bundled default fallback forever even though
          // `profiles.avatar_preset_id` points to a valid catalog row.
          if (!avatar && supabase) {
            try {
              const { data } = await supabase
                .from('profile_avatars')
                .select('*')
                .eq('id', presetId)
                .maybeSingle()
              if (data) avatar = data as Avatar
            } catch (err) {
              console.warn('[useUserDisplayProfile] Avatar fallback fetch failed:', err)
            }
          }
        }

        if (!cancelled) {
          // Cache the size-independent result so later consumers mount with it
          // synchronously (no loading frame, no default-author flash).
          const resolved: ResolvedProfileCache = {
            avatar,
            fullName: fullName ?? null,
            avatarPresetId: presetId ?? null,
            email: authUser?.email ?? null,
          }
          // Den Anzeigenamen für die Anmeldeseite mitschreiben. Sie hat keine
          // Sitzung und kann nichts abfragen; ohne diese Zeile bliebe auf dem
          // Gerät der Stand stehen, der zuletzt HIER gesetzt wurde — ein auf
          // einem anderen Gerät gewählter Spitzname käme nie an.
          rememberDisplayName(resolved.fullName)
          resolvedCache = resolved
          setProfile(profileFromCache(resolved, displaySize))
        }
      } catch (err) {
        console.error('[useUserDisplayProfile] Failed to load profile:', err)
        if (!cancelled) {
          setProfile({ avatarUrl: null, fullName: null, avatarPresetId: null, email: null, isLoading: false })
        }
      }
    }

    load()
    // Profile-mutation broadcasts (avatar/name from settings, cross-window via
    // IPC bridge) trigger a refetch so the sidebar updates without reload.
    unsubscribe = onAppEvent('user-profile-updated', () => {
      load()
    })

    // Änderungen von einem ANDEREN Gerät (Profilbild in der Desktop-App
    // gewechselt) meldet kein Signal — Profile laufen nicht über den Sync.
    // Die Seitenleiste bleibt die ganze Sitzung eingehängt und zeigte deshalb
    // das alte Bild, während frisch geöffnete Seiten schon das neue hatten
    // (25.09.2026). Also: neu nachfragen, wenn das Fenster zurückkommt.
    const refreshOnReturn = () => {
      if (document.visibilityState === 'hidden') return
      if (!isThrottleElapsed(Date.now(), lastLoadAt, REFOCUS_REFRESH_MS)) return
      load()
    }
    window.addEventListener('focus', refreshOnReturn)
    document.addEventListener('visibilitychange', refreshOnReturn)

    return () => {
      cancelled = true
      unsubscribe?.()
      window.removeEventListener('focus', refreshOnReturn)
      document.removeEventListener('visibilitychange', refreshOnReturn)
    }
  }, [userId, displaySize])

  return profile
}
